/**
 * ProjectsPage component
 * Lists all user projects and allows creating new ones
 */

import { useState, useEffect } from 'react'
import projectsService from '../../services/projects'
import toast from 'react-hot-toast'
import Button from '../../components/common/Button'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import Modal from '../../components/common/Modal'
import ProjectForm from '../../components/projects/ProjectForm'
import ProjectCard from '../../components/projects/ProjectCard'

const ProjectsPage = () => {
  const [projects, setProjects] = useState([])
  const [deletedProjects, setDeletedProjects] = useState([])
  const [showDeleted, setShowDeleted] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    loadProjects()
  }, [])

  const loadProjects = async () => {
    try {
      setLoading(true)
      setError('')
      // Fetch all projects including deleted ones
      const data = await projectsService.listProjects(true)
      // Handle both paginated and non-paginated responses
      const allProjects = data.results || data

      // Separate active and deleted projects
      const active = allProjects.filter((p) => !p.is_deleted)
      const deleted = allProjects.filter((p) => p.is_deleted)

      setProjects(active)
      setDeletedProjects(deleted)
    } catch (err) {
      console.error('Error loading projects:', err)
      setError(err.response?.data?.detail || 'Failed to load projects')
      toast.error('Failed to load projects')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateProject = async (formData) => {
    try {
      setCreating(true)
      await projectsService.createProject(formData)
      toast.success('Project created successfully!')
      setIsCreateModalOpen(false)
      // Reload projects list to get full data including member counts and user role
      await loadProjects()
    } catch (err) {
      console.error('Error creating project:', err)
      const errorMsg =
        err.response?.data?.detail || err.response?.data?.name?.[0] || 'Failed to create project'
      toast.error(errorMsg)
    } finally {
      setCreating(false)
    }
  }

  const handleDeleteProject = async (projectId) => {
    const project = projects.find((p) => p.id === projectId)
    if (!project) return

    try {
      // Optimistically move from active to deleted
      setProjects((prev) => prev.filter((p) => p.id !== projectId))
      setDeletedProjects((prev) => [
        ...prev,
        { ...project, is_deleted: true, deleted_at: new Date().toISOString() },
      ])

      await projectsService.deleteProject(projectId)
      toast.success('Project deleted successfully')
    } catch (err) {
      console.error('Error deleting project:', err)
      toast.error('Failed to delete project')
      // Reload on error to restore accurate state
      await loadProjects()
    }
  }

  const handleRestoreProject = async (projectId) => {
    const project = deletedProjects.find((p) => p.id === projectId)
    if (!project) return

    try {
      // Optimistically move from deleted to active
      setDeletedProjects((prev) => prev.filter((p) => p.id !== projectId))
      setProjects((prev) => [...prev, { ...project, is_deleted: false, deleted_at: null }])

      await projectsService.restoreProject(projectId)
      toast.success('Project restored successfully')
    } catch (err) {
      console.error('Error restoring project:', err)
      toast.error('Failed to restore project')
      // Reload on error to restore accurate state
      await loadProjects()
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Projects</h1>
          <p className="mt-2 text-sm text-gray-600">Manage your traffic analysis projects</p>
        </div>
        <div className="flex items-center gap-4">
          {deletedProjects.length > 0 && (
            <button
              onClick={() => setShowDeleted(!showDeleted)}
              className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
              {showDeleted ? 'Hide' : 'Show'} Deleted ({deletedProjects.length})
            </button>
          )}
          <Button variant="primary" onClick={() => setIsCreateModalOpen(true)}>
            <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            New Project
          </Button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Projects grid */}
      {projects.length === 0 ? (
        <div className="text-center py-12">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No projects</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by creating a new project.</p>
          <div className="mt-6">
            <Button variant="primary" onClick={() => setIsCreateModalOpen(true)}>
              <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              New Project
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <ProjectCard key={project.id} project={project} onDelete={handleDeleteProject} />
          ))}
        </div>
      )}

      {/* Deleted Projects Section */}
      {showDeleted && deletedProjects.length > 0 && (
        <div className="mt-12">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <svg
              className="w-5 h-5 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
            Deleted Projects
            <span className="text-sm text-gray-500 font-normal">
              (Will be permanently deleted after 30 days)
            </span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {deletedProjects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                onRestore={handleRestoreProject}
                isDeleted
              />
            ))}
          </div>
        </div>
      )}

      {/* Create Project Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => !creating && setIsCreateModalOpen(false)}
        title="Create New Project"
      >
        <ProjectForm
          onSubmit={handleCreateProject}
          onCancel={() => setIsCreateModalOpen(false)}
          loading={creating}
        />
      </Modal>
    </div>
  )
}

export default ProjectsPage
